const TOP_LEVEL_KEYS = [
  'schemaVersion', 'templateId', 'title', 'kind', 'orientation', 'stacked', 'categories', 'series',
  'axes', 'grid', 'numberFormat', 'showLegend', 'startAtZero',
]
const VALUE_SERIES_KEYS = ['name', 'type', 'axis', 'color', 'values']
const BUBBLE_SERIES_KEYS = ['name', 'type', 'axis', 'color', 'points']
const VALUE_TYPES = new Set(['bar', 'line', 'area', 'pie'])
const CHART_KINDS = new Set(['bar', 'line', 'area', 'pie', 'bubble', 'combo'])
const AXIS_NAMES = new Set(['primary', 'secondary'])
const COLOR = /^#[0-9a-fA-F]{6}$/
const TEMPLATE_ID = /^chart_[0-9a-f]{24}$/

export class ChartContractError extends Error {
  constructor(message) {
    super(message)
    this.name = 'ChartContractError'
    this.code = 'invalid_chart_contract'
  }
}

export function validateChartContract(payload) {
  objectWithKeys(payload, TOP_LEVEL_KEYS, 'chart')
  required(payload.schemaVersion === 1, 'Unsupported chart schema version')
  required(typeof payload.templateId === 'string' && TEMPLATE_ID.test(payload.templateId), 'Invalid template ID')
  text(payload.title, 300, false, 'title')
  required(CHART_KINDS.has(payload.kind), 'Invalid chart kind')
  required(payload.orientation === 'vertical' || payload.orientation === 'horizontal', 'Invalid orientation')
  boolean(payload.stacked, 'stacked')
  stringArray(payload.categories, 100000, 1000, 'categories')
  required(Array.isArray(payload.series) && payload.series.length > 0 && payload.series.length <= 100,
    'Invalid series count')
  axes(payload.axes)
  grid(payload.grid)
  numberFormat(payload.numberFormat)
  boolean(payload.showLegend, 'showLegend')
  boolean(payload.startAtZero, 'startAtZero')

  const types = new Set()
  for (const series of payload.series) {
    validateSeries(series, payload.categories.length, payload.axes.secondary !== null)
    types.add(series.type)
  }
  if (payload.kind === 'bubble') {
    required(payload.series.every((series) => series.type === 'bubble') && payload.categories.length === 0,
      'Invalid bubble chart shape')
  } else {
    required(payload.series.every((series) => series.type !== 'bubble'), 'Unexpected bubble series')
  }
  if (payload.kind === 'pie') {
    required(payload.series.length === 1 && payload.series[0].type === 'pie'
      && payload.series[0].axis === 'primary', 'Invalid pie chart shape')
  }
  if (payload.kind === 'combo') required(payload.series.length >= 2 && types.size >= 2, 'Invalid combo chart shape')
  return payload
}

export function toEChartsOption(contract) {
  const chart = validateChartContract(contract)
  const formatValue = numberFormatter(chart.numberFormat)
  const common = {
    animation: false,
    aria: { enabled: true, decal: { show: true } },
    title: { text: chart.title, left: 'center' },
    legend: { show: chart.showLegend, type: 'scroll', top: 34 },
    tooltip: { trigger: chart.kind === 'pie' || chart.kind === 'bubble' ? 'item' : 'axis', valueFormatter: formatValue },
  }

  if (chart.kind === 'pie') {
    const source = chart.series[0]
    return {
      ...common,
      series: [{
        name: source.name, type: 'pie', radius: ['18%', '65%'],
        itemStyle: colorStyle(source.color),
        data: chart.categories.map((name, index) => ({ name, value: source.values[index] })),
      }],
    }
  }

  if (chart.kind === 'bubble') {
    return {
      ...common,
      grid: { left: 58, right: 28, top: 76, bottom: 48, containLabel: true },
      xAxis: valueAxis(chart.axes.primary, chart.grid.showX, chart.startAtZero, formatValue),
      yAxis: valueAxis(chart.axes.secondary ?? chart.axes.primary, chart.grid.showY, chart.startAtZero, formatValue),
      series: chart.series.map((source) => ({
        name: source.name, type: 'scatter', yAxisIndex: source.axis === 'secondary' ? 1 : 0,
        itemStyle: colorStyle(source.color),
        symbolSize: (value) => Math.max(6, Math.min(80, Number(value[2]) || 0)),
        data: source.points.map((point) => [point.x, point.y, point.size, point.label]),
      })),
    }
  }

  const horizontal = chart.orientation === 'horizontal'
  const category = categoryAxis(chart.categories, horizontal ? chart.grid.showY : chart.grid.showX)
  const values = valueAxes(chart, horizontal ? chart.grid.showX : chart.grid.showY, formatValue)
  return {
    ...common,
    grid: { left: 58, right: chart.axes.secondary ? 58 : 28, top: 76, bottom: 48, containLabel: true },
    xAxis: horizontal ? values : category,
    yAxis: horizontal ? category : values,
    series: chart.series.map((source) => ({
      name: source.name,
      type: source.type === 'area' ? 'line' : source.type,
      ...(source.type === 'area' ? { areaStyle: {} } : {}),
      ...(chart.stacked ? { stack: 'total' } : {}),
      ...(source.axis === 'secondary' ? { [horizontal ? 'xAxisIndex' : 'yAxisIndex']: 1 } : {}),
      itemStyle: colorStyle(source.color),
      lineStyle: source.color ? { color: source.color } : undefined,
      data: source.values,
    })),
  }
}

export function shouldFallbackToJpeg(error) {
  return error?.code === 'unsupported_chart_mapping'
}

function validateSeries(series, categoryCount, hasSecondaryAxis) {
  required(isPlainObject(series), 'Invalid series')
  const bubble = series.type === 'bubble'
  objectWithKeys(series, bubble ? BUBBLE_SERIES_KEYS : VALUE_SERIES_KEYS, 'series')
  text(series.name, 300, false, 'series name')
  required(bubble || VALUE_TYPES.has(series.type), 'Invalid series type')
  required(AXIS_NAMES.has(series.axis), 'Invalid series axis')
  required(series.axis !== 'secondary' || hasSecondaryAxis, 'Missing secondary axis')
  required(series.color === null || (typeof series.color === 'string' && COLOR.test(series.color)), 'Invalid color')
  if (bubble) {
    required(Array.isArray(series.points) && series.points.length > 0 && series.points.length <= 100000,
      'Invalid bubble points')
    for (const point of series.points) {
      objectWithKeys(point, ['x', 'y', 'size', 'label'], 'bubble point')
      finite(point.x, 'bubble x')
      finite(point.y, 'bubble y')
      finite(point.size, 'bubble size')
      required(point.size >= 0, 'Invalid bubble size')
      required(point.label === null || (typeof point.label === 'string' && point.label.length <= 1000),
        'Invalid bubble label')
    }
    return
  }
  required(Array.isArray(series.values) && series.values.length <= 100000
    && series.values.length === categoryCount, 'Invalid series values')
  for (const value of series.values) if (value !== null) finite(value, 'series value')
}

function axes(value) {
  objectWithKeys(value, ['primary', 'secondary'], 'axes')
  axis(value.primary, 'primary axis')
  if (value.secondary !== null) axis(value.secondary, 'secondary axis')
}

function axis(value, label) {
  objectWithKeys(value, ['title', 'showLabels'], label)
  required(value.title === null || (typeof value.title === 'string' && value.title.length <= 300), `Invalid ${label} title`)
  boolean(value.showLabels, `${label} showLabels`)
}

function grid(value) {
  objectWithKeys(value, ['showX', 'showY'], 'grid')
  boolean(value.showX, 'grid.showX')
  boolean(value.showY, 'grid.showY')
}

function numberFormat(value) {
  if (value === null) return
  objectWithKeys(value, ['fractionDigits', 'decimalSeparator', 'groupingSeparator'], 'numberFormat')
  required(Number.isInteger(value.fractionDigits) && value.fractionDigits >= 0 && value.fractionDigits <= 20,
    'Invalid fraction digits')
  required(typeof value.decimalSeparator === 'string' && value.decimalSeparator.length === 1,
    'Invalid decimal separator')
  required(typeof value.groupingSeparator === 'string' && value.groupingSeparator.length === 1,
    'Invalid grouping separator')
}

function categoryAxis(data, showGrid) {
  return { type: 'category', data, axisLabel: { show: true }, splitLine: { show: showGrid } }
}

function valueAxes(chart, showGrid, formatter) {
  const primary = valueAxis(chart.axes.primary, showGrid, chart.startAtZero, formatter)
  if (!chart.axes.secondary) return primary
  return [primary, valueAxis(chart.axes.secondary, showGrid, chart.startAtZero, formatter)]
}

function valueAxis(axis, showGrid, startAtZero, formatter) {
  return {
    type: 'value', name: axis.title ?? '', min: startAtZero ? 0 : undefined,
    axisLabel: { show: axis.showLabels, formatter }, splitLine: { show: showGrid },
  }
}

function numberFormatter(format) {
  if (!format) return (value) => String(value ?? '')
  return (value) => {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return ''
    const [whole, fraction] = Number(value).toFixed(format.fractionDigits).split('.')
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, format.groupingSeparator)
    return fraction === undefined ? grouped : `${grouped}${format.decimalSeparator}${fraction}`
  }
}

function colorStyle(color) {
  return color ? { color } : undefined
}

function objectWithKeys(value, keys, label) {
  required(isPlainObject(value), `Invalid ${label}`)
  const actual = Object.keys(value).sort()
  const expected = [...keys].sort()
  required(actual.length === expected.length && actual.every((key, index) => key === expected[index]),
    `Unexpected ${label} fields`)
}

function stringArray(value, maxItems, maxLength, label) {
  required(Array.isArray(value) && value.length <= maxItems, `Invalid ${label}`)
  for (const item of value) text(item, maxLength, true, label)
}

function text(value, maxLength, emptyAllowed, label) {
  required(typeof value === 'string' && value.length <= maxLength && (emptyAllowed || value.trim().length > 0),
    `Invalid ${label}`)
}

function boolean(value, label) {
  required(typeof value === 'boolean', `Invalid ${label}`)
}

function finite(value, label) {
  required(typeof value === 'number' && Number.isFinite(value), `Invalid ${label}`)
}

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function required(condition, message) {
  if (!condition) throw new ChartContractError(message)
}
